import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DoCheck,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  Optional,
  Self,
  signal,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { MAT_FORM_FIELD, MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';

@Component({
  selector: 'app-autocomplete-multi-input',
  templateUrl: './autocomplete-multi-input.component.html',
  styleUrls: ['./autocomplete-multi-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: MatFormFieldControl, useExisting: AutocompleteMultiInputComponent }],
})
export class AutocompleteMultiInputComponent<T> implements ControlValueAccessor, MatFormFieldControl<T[]>, OnDestroy, DoCheck {
  static nextId = 0;

  @ViewChild('value') valueInput: ElementRef<HTMLInputElement>;

  @Input({ required: true }) options: T[];
  @Input({ required: true }) getDisplayName: (value: T) => string;
  @Input({ required: true }) getValueId: (value: T) => string;
  @Input() userAriaDescribedBy: string;

  public readonly autocompleteOrigin: FormFieldAutocompleteOrigin;

  public readonly valueInputControl = new FormControl<T | null>(null);

  stateChanges = new Subject<void>();
  focused = false;
  touched = false;
  controlType = 'app-autocomplete-input';
  id = `example-tel-input-${AutocompleteMultiInputComponent.nextId++}`;
  filteredOptions = computed(() =>
    this.sortOptionItems(
      this.options.filter(
        (o) =>
          this.valueItems().every((i) => this.getValueId(i) !== this.getValueId(o)) &&
          this.getDisplayName(o).toLowerCase().includes(this._optionFilter().toLowerCase()),
      ),
    ),
  );
  valueItems = signal<T[]>([]);
  displayedValueItems = computed(() => this.sortOptionItems(this.valueItems()));

  private _errorState = false;
  private _placeholder: string;
  private _required = false;
  private _disabled = false;
  private _optionFilter = signal('');

  constructor(
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    @Optional() @Inject(MAT_FORM_FIELD) public _formField: MatFormField,
    @Optional() @Self() public ngControl: NgControl,
    @Optional() private _parentForm: NgForm,
    @Optional() private _parentFormGroup: FormGroupDirective,
  ) {
    this.valueInputControl.valueChanges.subscribe((value) => {
      if (value) {
        const updatedValueItems = this.sortOptionItems([...this.valueItems(), value]);
        this.valueItems.set(updatedValueItems);
        this.onChange(updatedValueItems);
        this.handleInput('');
      }
    });
    this.autocompleteOrigin = new FormFieldAutocompleteOrigin(_formField);
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  get empty() {
    return !this.valueItems().length;
  }

  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get errorState(): boolean {
    return this._errorState;
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    if (this._disabled) {
      this.valueInputControl.disable();
    } else {
      this.valueInputControl.enable();
    }
    this.stateChanges.next();
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  @Input()
  get value(): T[] | null {
    return this.valueItems();
  }
  set value(value: T[] | null) {
    this.valueItems.set(value ?? []);
    this.stateChanges.next();
  }

  removeValueItem(item: T) {
    const updatedValueItems = this.valueItems().filter((i) => this.getValueId(i) !== this.getValueId(item));
    this.valueItems.set(updatedValueItems);
    this.onChange(updatedValueItems);
  }

  autocompleteDisplayWith = (item: T | null) => (item ? this.getDisplayName(item) : '');

  onChange = (_: unknown) => {};
  onTouched = () => {};

  ngOnDestroy() {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  ngDoCheck() {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  onInput(event: Event) {
    this.handleInput((event.target as HTMLInputElement).value);
  }

  onFocus(event: Event) {
    this.setOptionFilter((event.target as HTMLInputElement).value);
  }

  onFocusIn() {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut() {
    if (this.focused) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  setDescribedByIds(ids: string[]) {
    this.valueInput?.nativeElement.setAttribute('aria-describedby', ids.join(' '));
  }

  onContainerClick() {
    this._focusMonitor.focusVia(this.valueInput, 'program');
  }

  writeValue(value: T[] | null): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleInput(value: string) {
    if (value === '') {
      this.valueInputControl.setValue(null);
    }

    this.setOptionFilter(value);
  }

  setOptionFilter(value: string) {
    this._optionFilter.set(value);
  }

  private updateErrorState() {
    const parent = this._parentFormGroup || this._parentForm;

    const oldState = this._errorState;
    const newState = !!this.ngControl?.invalid && (this.touched || parent.submitted);

    if (oldState !== newState) {
      this._errorState = newState;
      this.stateChanges.next();
    }
  }

  private sortOptionItems(items: T[]): T[] {
    return [...items].sort((a, b) => this.getDisplayName(a).localeCompare(this.getDisplayName(b)));
  }
}

class FormFieldAutocompleteOrigin {
  constructor(private formField: MatFormField) {}

  // must implement same interface as MatAutocompleteOrigin
  // noinspection JSUnusedGlobalSymbols
  get elementRef(): ElementRef<HTMLElement> {
    return this.formField._textField;
  }
}
